import { useEffect } from 'react';
import { FallbackProps } from 'react-error-boundary';

import Header from 'components/Header';
import { Button } from 'components/Input/Button';
import Typography from 'components/Typography';
import { useView } from 'hooks/view';
import { ACTIONS } from 'constants/hook';

import StaticError from '../Extensions/StaticError';
import {
  BUTTON_CTA_TEXT,
  ERROR_MESSAGE_NO_RETRY,
  ERROR_MESSAGE_YES_RETRY,
  MAX_CTA_SEEN_COUNT,
} from './constants';
import {
  AnimationTextContainer,
  StyledAlertIcon,
  StyledErrorContainer,
  StyledErrorWrapper,
} from './Fallback.styles';

import { htmlId } from 'utils/html';
import { END_CHAT } from 'components/Layout/EndAdvocateChatModal/content';

const DefaultFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const { viewState, dispatchViewAction } = useView();

  const seenCount = viewState.errorBoundary.seenCount;

  useEffect(() => {
    dispatchViewAction({
      type: ACTIONS.CHANGE,
      payload: {
        ...viewState,
        errorBoundary: {
          ...viewState.errorBoundary,
          isVisible: true,
          seenCount: seenCount + 1,
        },
      },
    });
  }, []);

  const handleTriggerRetry = () => {
    dispatchViewAction({
      type: ACTIONS.CHANGE,
      payload: {
        ...viewState,
        errorBoundary: {
          ...viewState.errorBoundary,
          isVisible: false,
        },
      },
    });
    resetErrorBoundary();
  };

  const getCtaContent = () => {
    const hideCtaButton = error instanceof StaticError;
    if (hideCtaButton || seenCount >= MAX_CTA_SEEN_COUNT) {
      return (
        <Typography
          id={htmlId('default-fallback-error-message-no-retry')}
          size='lg'
          variant='label'
          as='span'
        >
          {ERROR_MESSAGE_NO_RETRY}
        </Typography>
      );
    }
    return (
      <Button id={htmlId('default-fallback-retry-button')} onClick={handleTriggerRetry}>
        {BUTTON_CTA_TEXT}
      </Button>
    );
  };

  return (
    <StyledErrorContainer id={htmlId('default-fallback-error-container')}>
      <Header
        id={htmlId('default-fallback-header')}
        endChatModalType={END_CHAT}
        showEndChatModal={false}
        hideEndChatButton={true}
        handleEndChatModalType={() => {}}
        handleHideEndChat={() => {}}
        toggleEndChatModal={() => {}}
      />
      <StyledErrorWrapper id={htmlId('default-fallback-error-wrapper')} role='alert'>
        <AnimationTextContainer id={htmlId('default-fallback-animation-text-container')}>
          <StyledAlertIcon />
          <Typography
            id={htmlId('default-fallback-error-message-with-retry')}
            size='lg'
            variant='label'
            as='span'
          >
            {ERROR_MESSAGE_YES_RETRY}
          </Typography>
        </AnimationTextContainer>
        {getCtaContent()}
      </StyledErrorWrapper>
    </StyledErrorContainer>
  );
};

export default DefaultFallback;
